import { Close, ContentCopy, Download, PixOutlined } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import QRCode from "qrcode.react";
import { useState } from "react";
import { colorPrimary } from "../../../theme";

interface QrCodeModalProps {
  open: boolean;
  onClose: () => void;
  qrCodeData: string | null;
  pixCopyPaste?: string | null;
  title?: string;
}

const steps = [
  "Abra o app do seu banco ou carteira digital",
  "Escolha a opção Pix e depois QR Code",
  "Escaneie o código com a câmera do celular",
  "Confirme os dados e finalize o pagamento",
];

export function QrCodeModal({
  open,
  onClose,
  qrCodeData,
  pixCopyPaste,
  title = "QR Code de Pagamento",
}: QrCodeModalProps) {
  const [copied, setCopied] = useState(false);

  const pixCode = pixCopyPaste || qrCodeData;

  const handleCopy = () => {
    if (!pixCode) return;
    navigator.clipboard.writeText(pixCode).then(() => setCopied(true));
  };

  const handleDownload = () => {
    const svg = document.getElementById("qr-code-svg");
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    canvas.width = 300;
    canvas.height = 300;

    img.onload = () => {
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.download = "qrcode-pagamento.png";
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = "data:image/svg+xml;base64," + btoa(svgData);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${colorPrimary} 0%, #ff8c42 100%)`,
            px: 3,
            py: 2.5,
            position: "relative",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <PixOutlined sx={{ color: "white", fontSize: 22 }} />
            <Typography fontSize="1.1rem" fontWeight={700} color="white">
              {title}
            </Typography>
          </Stack>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              color: "rgba(255,255,255,0.8)",
              "&:hover": { color: "white", bgcolor: "rgba(255,255,255,0.15)" },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          <Stack>
            {/* QR Code */}
            <Box
              sx={{
                bgcolor: "#fafafa",
                py: 4,
                display: "flex",
                justifyContent: "center",
              }}
            >
              {qrCodeData ? (
                <Box
                  sx={{
                    bgcolor: "white",
                    p: 2.5,
                    borderRadius: 3,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <QRCode
                    id="qr-code-svg"
                    value={qrCodeData}
                    size={200}
                    level="H"
                    bgColor="#ffffff"
                    fgColor="#111827"
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    width: 200,
                    height: 200,
                    bgcolor: "#f0f0f0",
                    borderRadius: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography color="#aaa" fontSize="0.8rem" textAlign="center">
                    Nenhum QR Code<br />disponível
                  </Typography>
                </Box>
              )}
            </Box>

            <Stack spacing={0} divider={<Divider />}>
              {/* Pix Copia e Cola */}
              {pixCode && (
                <Box sx={{ px: 3, py: 2.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                    <ContentCopy sx={{ fontSize: 15, color: colorPrimary }} />
                    <Typography fontSize="0.8rem" fontWeight={700} color="#111827" letterSpacing={0.3}>
                      PIX COPIA E COLA
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      bgcolor: "#f8fafc",
                      border: "1.5px solid #e2e8f0",
                      borderRadius: 2,
                      px: 2,
                      py: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: colorPrimary,
                        bgcolor: "#fff7f0",
                      },
                    }}
                    onClick={handleCopy}
                  >
                    <Typography
                      fontSize="0.7rem"
                      color="#475569"
                      sx={{
                        flex: 1,
                        wordBreak: "break-all",
                        fontFamily: "monospace",
                        lineHeight: 1.6,
                      }}
                    >
                      {pixCode}
                    </Typography>
                    <Tooltip title={copied ? "Copiado!" : "Clique para copiar"} placement="top">
                      <Box
                        sx={{
                          flexShrink: 0,
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          bgcolor: copied ? "#dcfce7" : "#f1f5f9",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.2s",
                        }}
                      >
                        <ContentCopy
                          sx={{
                            fontSize: 15,
                            color: copied ? "#16a34a" : "#64748b",
                          }}
                        />
                      </Box>
                    </Tooltip>
                  </Box>

                  <Box
                    component="button"
                    onClick={handleCopy}
                    sx={{
                      mt: 1.5,
                      width: "100%",
                      py: 1.25,
                      border: `1.5px solid ${copied ? "#16a34a" : colorPrimary}`,
                      bgcolor: copied ? "#dcfce7" : "transparent",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      cursor: "pointer",
                      transition: "all 0.2s",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      color: copied ? "#16a34a" : colorPrimary,
                      "&:hover": {
                        bgcolor: copied ? "#bbf7d0" : "#fff7f0",
                      },
                    }}
                  >
                    <ContentCopy sx={{ fontSize: 15 }} />
                    {copied ? "Código copiado!" : "Copiar código Pix"}
                  </Box>
                </Box>
              )}

              {/* Instruções */}
              <Box sx={{ px: 3, py: 2.5 }}>
                <Typography
                  fontSize="0.8rem"
                  fontWeight={700}
                  color="#111827"
                  letterSpacing={0.3}
                  mb={1.5}
                >
                  COMO PAGAR
                </Typography>
                <Stack spacing={1.25}>
                  {steps.map((step, i) => (
                    <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          background: `linear-gradient(135deg, ${colorPrimary} 0%, #ff8c42 100%)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          mt: 0.1,
                        }}
                      >
                        <Typography fontSize="0.65rem" fontWeight={800} color="white">
                          {i + 1}
                        </Typography>
                      </Box>
                      <Typography fontSize="0.78rem" color="#475569" lineHeight={1.5}>
                        {step}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>

              {/* Botão Download */}
              {qrCodeData && (
                <Box sx={{ px: 3, py: 2 }}>
                  <Box
                    component="button"
                    onClick={handleDownload}
                    sx={{
                      width: "100%",
                      py: 1.25,
                      background: `linear-gradient(135deg, ${colorPrimary} 0%, #ff8c42 100%)`,
                      border: "none",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      cursor: "pointer",
                      transition: "all 0.2s",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      color: "white",
                      boxShadow: "0 4px 12px rgba(255, 107, 0, 0.25)",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: "0 6px 16px rgba(255, 107, 0, 0.35)",
                      },
                      "&:active": { transform: "translateY(0)" },
                    }}
                  >
                    <Download sx={{ fontSize: 16 }} />
                    Baixar QR Code
                  </Box>
                </Box>
              )}
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={copied}
        autoHideDuration={2500}
        onClose={() => setCopied(false)}
        message="Código Pix copiado!"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
}
